import { Factory, Gauge, Settings, TrendingUp, CheckCircle2, Zap } from 'lucide-react';

const Manufacturing = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_pslcote-labels/artifacts/yeqedwnn_ChatGPT%20Image%20Nov%2017%2C%202025%2C%2010_48_00%20AM.png" 
            alt="Manufacturing Machinery"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/30"></div>
        </div>
        <div className="section-container relative z-10">
          <h1 className="text-4xl lg:text-5xl font-bold text-white text-center mb-4 drop-shadow-lg" data-testid="manufacturing-page-title">Manufacturing Facility</h1>
          <p className="text-lg lg:text-xl text-white text-center mx-auto max-w-3xl drop-shadow-md">
            Backward integrated, state-of-the-art manufacturing with cutting-edge machinery and processes
          </p>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 lg:py-24">
        <div className="section-container">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-3xl font-bold mb-6 pslcote-blue" data-testid="facility-overview-title">Our Manufacturing Excellence</h2>
            <p className="text-gray-700 leading-relaxed text-base">
              Our state-of-the-art manufacturing facility is equipped with cutting-edge machinery that enables us to maintain superior quality control, ensure consistent production standards, and deliver custom label stock solutions with precision and efficiency.
            </p>
          </div>

          {/* Machinery Cards */}
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-8 border-l-4 border-[#0066cc]" data-testid="slitter-rewinder-card">
              <div className="flex items-start gap-4 mb-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <Settings className="text-[#0066cc]" size={32} />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Slitter Rewinder Machine</h3>
                  <p className="text-sm text-gray-500">Precision Cutting & Rewinding</p>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed">
                High-precision slitter rewinder ensures accurate cutting and rewinding of label stock materials, maintaining consistent width, tension, and edge quality across production batches.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8 border-l-4 border-[#0066cc]" data-testid="silicone-coater-card">
              <div className="flex items-start gap-4 mb-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <Gauge className="text-[#0066cc]" size={32} />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Silicone Coater Machine</h3>
                  <p className="text-sm text-gray-500">Release Liner Processing</p>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Specialized coating equipment for applying precise silicone layers to release liners, ensuring optimal release properties and consistent performance in high-speed labeling applications.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8 border-l-4 border-[#0066cc]" data-testid="adhesive-coating-card">
              <div className="flex items-start gap-4 mb-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <Factory className="text-[#0066cc]" size={32} />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Adhesive Coating Machine</h3>
                  <p className="text-sm text-gray-500">Hotmelt: 1020mm width / 200MPM</p>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Advanced coating technology allowing precise application of various adhesive formulations, ensuring uniform coating thickness and optimal adhesion properties for different substrates.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-8 border-l-4 border-[#0066cc]" data-testid="coating-systems-card">
              <div className="flex items-start gap-4 mb-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <Zap className="text-[#0066cc]" size={32} />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Dual Coating Systems</h3>
                  <p className="text-sm text-gray-500">Hotmelt & Water-Based</p>
                </div>
              </div>
              <p className="text-gray-600 leading-relaxed">
                Industrial-grade hotmelt and water-based adhesive application systems for pressure-sensitive adhesives with enhanced performance characteristics across diverse applications.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Production Capacity */}
      <section className="bg-gradient-to-br from-[#0066cc] to-[#0052a3] py-16 lg:py-24 text-white">
        <div className="section-container">
          <div className="text-center">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4" data-testid="production-capacity-number">
              50 Lakh Sq. Meter
            </h2>
            <p className="text-xl lg:text-2xl opacity-90 mb-12">Monthly Production Capacity</p>
          </div>
        </div>
      </section>

      {/* Strategic Advantages */}
      <section className="py-16 lg:py-24">
        <div className="section-container">
          <h2 className="section-title text-center mb-12" data-testid="strategic-advantages-title">Strategic Advantages</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            <div className="text-center p-6">
              <div className="bg-blue-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="text-[#0066cc]" size={36} />
              </div>
              <h3 className="font-bold text-lg mb-2">Rapid Market Response</h3>
              <p className="text-gray-600 text-sm">
                Ability to swiftly scale production and meet urgent client demands without infrastructure constraints
              </p>
            </div>

            <div className="text-center p-6">
              <div className="bg-blue-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="text-[#0066cc]" size={36} />
              </div>
              <h3 className="font-bold text-lg mb-2">Growth Opportunity</h3>
              <p className="text-gray-600 text-sm">
                Significant headroom for business expansion without additional capital investment in facilities
              </p>
            </div>

            <div className="text-center p-6">
              <div className="bg-blue-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="text-[#0066cc]" size={36} />
              </div>
              <h3 className="font-bold text-lg mb-2">Production Flexibility</h3>
              <p className="text-gray-600 text-sm">
                Capability to accommodate diverse product requirements simultaneously, even during peak demand
              </p>
            </div>

            <div className="text-center p-6">
              <div className="bg-blue-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Factory className="text-[#0066cc]" size={36} />
              </div>
              <h3 className="font-bold text-lg mb-2">Quality Stability</h3>
              <p className="text-gray-600 text-sm">
                Backward integration ensures consistent quality standards and delivery reliability
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="bg-gray-50 py-16 lg:py-24">
        <div className="section-container">
          <h2 className="section-title text-center mb-12" data-testid="manufacturing-features-title">Manufacturing Capabilities</h2>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="flex items-start gap-3 bg-white p-6 rounded-lg">
              <CheckCircle2 className="text-[#0066cc] flex-shrink-0 mt-1" size={20} />
              <p className="text-gray-700">Backward integrated manufacturing for consistent quality and cost optimization</p>
            </div>

            <div className="flex items-start gap-3 bg-white p-6 rounded-lg">
              <CheckCircle2 className="text-[#0066cc] flex-shrink-0 mt-1" size={20} />
              <p className="text-gray-700">Hotmelt and water-based adhesive coating capabilities</p>
            </div>

            <div className="flex items-start gap-3 bg-white p-6 rounded-lg">
              <CheckCircle2 className="text-[#0066cc] flex-shrink-0 mt-1" size={20} />
              <p className="text-gray-700">High-precision slitter rewinder for accurate roll processing</p>
            </div>

            <div className="flex items-start gap-3 bg-white p-6 rounded-lg">
              <CheckCircle2 className="text-[#0066cc] flex-shrink-0 mt-1" size={20} />
              <p className="text-gray-700">Advanced release liner processing with silicone coating</p>
            </div>

            <div className="flex items-start gap-3 bg-white p-6 rounded-lg">
              <CheckCircle2 className="text-[#0066cc] flex-shrink-0 mt-1" size={20} />
              <p className="text-gray-700">Roll form label stocks compatible with 3" core industrial machines</p>
            </div>

            <div className="flex items-start gap-3 bg-white p-6 rounded-lg">
              <CheckCircle2 className="text-[#0066cc] flex-shrink-0 mt-1" size={20} />
              <p className="text-gray-700">Wide material processing: Paper, Film, Foil, PET, BOPP, PE, Synthetic</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Manufacturing;